import { call, put, select, takeLatest } from "redux-saga/effects";

import type {
  FiWeeklySummaryResolvedSettings,
  FiWeeklySummarySettings,
  FiWeeklySummarySettingsListResponse,
} from "@mapmycustomers/shared/types/entity/fieldInsight";
import type Organization from "@mapmycustomers/shared/types/Organization";
import { isApiError } from "@mapmycustomers/shared/util/assert";

import { fromBeSettingsCore } from "@app/component/executiveSummary/util/analysisConfigConversion";
import { toBeSettingsPayload } from "@app/scene/dashboard/components/ExecutiveSummaryContent/util/analysisSettingsConversion";
import findScopedSettingsRow from "@app/scene/dashboard/utils/findScopedSettingsRow";
import scopeToFilter from "@app/scene/dashboard/utils/scopeToFilter";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam/selectors";

import { fetchWeeklySummarySettings, saveWeeklySummarySettings } from "./actions";

// What an undeployed endpoint answers with, depending on how the gateway is configured.
const MISSING_ENDPOINT_STATUSES = [404, 405, 501];

export function* onFetchWeeklySummarySettings({
  payload,
}: ReturnType<typeof fetchWeeklySummarySettings>) {
  try {
    const { scope } = payload;
    const orgId: Organization["id"] = yield select(getOrganizationId);
    // The settings list is filtered to the caller's own rows, so an org preset never appears in
    // it and the form would show the built-in defaults instead of what the summary is actually
    // generated with. The resolved read answers that; it carries no id, so Save still creates
    // the caller's own row.
    let resolved: FiWeeklySummaryResolvedSettings | undefined;
    try {
      // Only the call is guarded. Converting and handing over sit outside, or a conversion bug
      // would read as a failed call and deliver a second, contradicting answer behind the first.
      resolved = yield callApi(
        "fetchFieldInsightsWeeklySummarySettingsInEffect",
        orgId,
        scopeToFilter(scope)
      );
    } catch (error) {
      // A web release can precede the API one, so fall back to the caller's own row until
      // /resolved is deployed everywhere — but only for the "not there yet" statuses. Any other
      // failure is re-thrown, so it is reported rather than quietly reinstating the very bug the
      // resolved read exists to fix, and a 401 still reaches the re-login handling.
      // TODO(mmc-server#10952): drop this fallback once /resolved has shipped.
      if (!isApiError(error) || !MISSING_ENDPOINT_STATUSES.includes(error.status ?? 0)) {
        throw error;
      }
      // Tolerated, not discarded: the toast is what a user must not see, so it is the toast that
      // is turned off. Skipping the handler outright would also drop the console output and the
      // reporting that says whether the fallback is still firing — the only evidence that could
      // ever retire the TODO. A 404 is not only "route missing"; it also answers an org, team or
      // user the server rejects, so those need to be visible somewhere too.
      yield put(handleError({ displayNotification: false, error }));
    }
    if (resolved) {
      yield call(payload.callback, fromBeSettingsCore(resolved));
      return;
    }
    const response: FiWeeklySummarySettingsListResponse = yield callApi(
      "fetchFieldInsightsWeeklySummarySettings",
      orgId,
      scopeToFilter(scope)
    );
    const row = findScopedSettingsRow(response.data, scope);
    yield call(payload.callback, fromBeSettingsCore(row));
  } catch (error) {
    yield put(handleError({ error }));
    if (payload.failureCallback) {
      yield call(payload.failureCallback);
    }
  }
}

export function* onSaveWeeklySummarySettings({
  payload,
}: ReturnType<typeof saveWeeklySummarySettings>) {
  try {
    const { config, scope } = payload;
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const body = toBeSettingsPayload(config, scope);

    const existing: FiWeeklySummarySettingsListResponse = yield callApi(
      "fetchFieldInsightsWeeklySummarySettings",
      orgId,
      scopeToFilter(scope)
    );
    const existingRow = findScopedSettingsRow(existing.data, scope);

    let saved: FiWeeklySummarySettings;
    if (existingRow) {
      saved = yield callApi(
        "updateFieldInsightsWeeklySummarySettings",
        orgId,
        existingRow.id,
        body
      );
    } else {
      saved = yield callApi("createFieldInsightsWeeklySummarySettings", orgId, body);
    }
    yield call(payload.callback, fromBeSettingsCore(saved));
  } catch (error) {
    yield put(handleError({ error }));
    if (payload.failureCallback) {
      yield call(payload.failureCallback);
    }
  }
}

export function* weeklySummarySettingsSagas() {
  yield takeLatest(fetchWeeklySummarySettings, onFetchWeeklySummarySettings);
  yield takeLatest(saveWeeklySummarySettings, onSaveWeeklySummarySettings);
}
